import getOr from 'lodash/fp/getOr';
import type { Store } from '@reduxjs/toolkit';
import type { Exception } from '@sentry/browser';

import { reportErrorToSentry } from '../setup/sentry';
import { changeLocale, displayMessagesFetched } from './langSlice';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { trace } from '../setup/trace';

const MODULE_NAME = 'driveradministration-web';

const sendError = (exception: Exception) => {
    reportErrorToSentry(exception, {
        tags: {
            module: MODULE_NAME,
        },
    });
};

// Webpack is weird sometimes, maybe it's Babel, who knows...
// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
const normalizeDynamicImport = (imported: any) => {
    return getOr(imported, 'default', imported);
};

export const importDisplayMessages = (locale: string) =>
    import(/* webpackChunkName: "[request]" */ `../../features/translations/${locale}.json`)
        .then(normalizeDynamicImport)
        .catch(error => {
            sendError(error);
            return error;
        });

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);
        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
            // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
        } catch (error: any) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error);
            store.dispatch(changeLocale(DEFAULT_LOCALE));
        }
    };
